import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Timer } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Countdown = ({ endAt, compact = false }) => {
    const [left, setLeft] = useState(null);
    useEffect(() => {
        const tick = () => {
            const ms = new Date(endAt) - new Date();
            if (ms <= 0) { setLeft(null); return; }
            const d = Math.floor(ms / 86400000);
            const h = String(Math.floor((ms % 86400000) / 3600000)).padStart(2, '0');
            const m = String(Math.floor((ms % 3600000) / 60000)).padStart(2, '0');
            const s = String(Math.floor((ms % 60000) / 1000)).padStart(2, '0');
            setLeft(`${d > 0 ? `${String(d).padStart(2, '0')} дни ` : ''}${h}:${m}:${s}`);
        };
        tick();
        const iv = setInterval(tick, 1000);
        return () => clearInterval(iv);
    }, [endAt]);
    if (!left) return null;
    return (
        <span className={`inline-flex items-center gap-1 bg-red-600 text-white rounded-full font-semibold ${compact ? 'text-[10px] px-2 py-0.5' : 'text-xs sm:text-sm px-3 py-1'}`} data-testid="promo-countdown">
            <Timer className={compact ? 'w-3 h-3' : 'w-4 h-4'} /> {compact ? left : `Приключва след: ${left}`}
        </span>
    );
};

const track = (promoId, event) => {
    axios.post(`${API}/promotions/${promoId}/track`, { event }).catch(() => {});
};

const BannerImage = ({ b, eager = false, className = '' }) => (
    <picture>
        {b.mobile_url && <source media="(max-width: 640px)" srcSet={`${API.replace('/api', '')}${b.mobile_url}`} />}
        <img
            src={`${API.replace('/api', '')}${b.desktop_url || b.mobile_url}`}
            alt={b.banner?.alt || b.banner?.title || 'Промоция'}
            className={className}
            loading={eager ? 'eager' : 'lazy'}
            fetchpriority={eager ? 'high' : 'auto'}
        />
    </picture>
);

const BannerOverlay = ({ b }) => {
    if (!b.banner?.show_text_overlay || (!b.banner?.title && !b.banner?.button_text)) return null;
    return (
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/35 to-transparent flex flex-col justify-center items-start p-6 sm:p-12">
            {b.banner.title && <h3 className="text-white font-bold text-xl sm:text-3xl lg:text-4xl max-w-[70%]" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}>{b.banner.title}</h3>}
            {b.banner.subtitle && <p className="text-white text-sm sm:text-lg mt-2 max-w-[60%]" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.8)' }}>{b.banner.subtitle}</p>}
            {b.end_at && <div className="mt-3"><Countdown endAt={b.end_at} /></div>}
            {b.banner.button_text && (
                <span className="mt-4 bg-amber-500 hover:bg-amber-600 text-white text-sm sm:text-base px-5 py-2.5 rounded-full font-semibold shadow-lg transition-colors">
                    {b.banner.button_text}
                </span>
            )}
        </div>
    );
};

const HeroSlider = ({ banners }) => {
    const navigate = useNavigate();
    const [idx, setIdx] = useState(0);
    const touchX = useRef(null);
    const sliderBanners = banners.filter(b => b.in_slider !== false);
    const count = sliderBanners.length;

    useEffect(() => {
        if (count <= 1) return;
        const iv = setInterval(() => setIdx(i => (i + 1) % count), 6000);
        return () => clearInterval(iv);
    }, [count]);

    useEffect(() => { sliderBanners.forEach(b => track(b.promo_id, 'view')); /* eslint-disable-next-line */ }, []);

    if (!count) return null;

    const go = (b) => { track(b.promo_id, 'click'); navigate(b.link); };

    return (
        <div className="relative rounded-2xl overflow-hidden shadow-lg" data-testid="promo-hero-slider"
            onTouchStart={e => { touchX.current = e.touches[0].clientX; }}
            onTouchEnd={e => {
                if (touchX.current === null) return;
                const dx = e.changedTouches[0].clientX - touchX.current;
                if (dx > 50) setIdx(i => (i - 1 + count) % count);
                else if (dx < -50) setIdx(i => (i + 1) % count);
                touchX.current = null;
            }}>
            <div className="flex transition-transform duration-500" style={{ transform: `translateX(-${idx * 100}%)` }}>
                {sliderBanners.map((b, i) => (
                    <div key={b.promo_id} className="w-full shrink-0 relative cursor-pointer" onClick={() => go(b)} data-testid={`hero-banner-${b.promo_id}`}>
                        <BannerImage b={b} eager={i === 0} className="w-full h-48 sm:h-72 lg:h-96 object-cover" />
                        <BannerOverlay b={b} />
                    </div>
                ))}
            </div>
            {count > 1 && (
                <>
                    <button aria-label="Предишен" onClick={(e) => { e.stopPropagation(); setIdx(i => (i - 1 + count) % count); }}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow"><ChevronLeft className="w-5 h-5" /></button>
                    <button aria-label="Следващ" onClick={(e) => { e.stopPropagation(); setIdx(i => (i + 1) % count); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow"><ChevronRight className="w-5 h-5" /></button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                        {sliderBanners.map((_, i) => (
                            <button key={i} onClick={(e) => { e.stopPropagation(); setIdx(i); }} aria-label={`Банер ${i + 1}`}
                                className={`w-2.5 h-2.5 rounded-full transition-colors ${i === idx ? 'bg-amber-500' : 'bg-white/70'}`} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

const BannerGrid = ({ banners, small = false }) => {
    const navigate = useNavigate();
    useEffect(() => { banners.forEach(b => track(b.promo_id, 'view')); /* eslint-disable-next-line */ }, []);
    if (!banners.length) return null;
    return (
        <div className={`grid gap-4 ${small ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2'}`}>
            {banners.map(b => (
                <div key={b.promo_id} className="relative rounded-xl overflow-hidden shadow cursor-pointer group"
                    onClick={() => { track(b.promo_id, 'click'); navigate(b.link); }} data-testid={`promo-banner-${b.promo_id}`}>
                    <BannerImage b={b} className={`w-full object-cover transition-transform duration-300 group-hover:scale-105 ${small ? 'h-32 sm:h-40' : 'h-40 sm:h-56'}`} />
                    <BannerOverlay b={b} />
                    {b.end_at && !b.banner?.show_text_overlay && (
                        <div className="absolute bottom-2 left-2"><Countdown endAt={b.end_at} compact /></div>
                    )}
                </div>
            ))}
        </div>
    );
};

export const PromoBanners = ({ placement }) => {
    const [banners, setBanners] = useState([]);
    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/promotions/banners`, { params: { placement } });
            setBanners(res.data.banners || []);
        } catch { /* noop */ }
    }, [placement]);
    useEffect(() => { load(); }, [load]);

    if (!banners.length) return null;
    if (placement === 'hero') return <HeroSlider banners={banners} />;
    return <BannerGrid banners={banners} small={placement === 'promo_cards'} />;
};

export default PromoBanners;
